import React, {RefObject, useEffect, useRef, useState} from 'react'
import styled, {useTheme} from 'styled-components'

import {useWistia, useWistiaVideoApi, useIntersectionObserver} from '@miro-site/core'

interface VideoProps {
  thumbnail?: string
  thumbnailAlt?: string
  id: string
  containerId?: string
  controls?: boolean
  togglePlay?: boolean
  time?: number
  rootRef?: RefObject<HTMLElement>
  options?: Parameters<typeof useWistiaVideoApi>[0]['options']
}

const KEYBOARD_KEYS = {
  SPACE: ' ',
  ENTER: 'Enter',
}

const WistiaImageWrapper = styled.div`
  position: relative;

  &:focus {
    ${({theme}) => theme.normalizeStyles({...theme.focus.outlines.default})}
  }

  img {
    width: 100%;
    max-width: 100%;
    cursor: pointer;
  }
`

const Video: React.FC<VideoProps> = ({
  thumbnail,
  thumbnailAlt,
  rootRef,
  id,
  containerId,
  controls,
  togglePlay,
  time,
  options,
}) => {
  const theme = useTheme()

  const [isThumbnailVisible, setIsThumbnailVisible] = useState(!!thumbnail)
  const [isInitialized, setIsInitialized] = useState(false)

  const videoId = id || containerId

  const wistiaRef = useRef<HTMLDivElement>(null)
  const thumbnailRef = useRef<HTMLDivElement>(null)

  const [containerRef, setContainerRef] = useState(null)
  const container = useIntersectionObserver(containerRef, {
    freezeOnceVisible: true,
    rootMargin: '300px 0px 300px 0px',
  })

  const isVisible = !!container?.isIntersecting

  useEffect(() => {
    if (rootRef?.current) {
      setContainerRef(rootRef)
      return
    }

    if (thumbnailRef?.current) {
      setContainerRef(thumbnailRef)
      return
    }

    if (wistiaRef?.current) {
      setContainerRef(wistiaRef)
      return
    }
  }, [rootRef, thumbnailRef, wistiaRef])

  const initWistiaVideo = useWistia()

  const videoApi = useWistiaVideoApi({
    id: videoId,
    jsonp: true,
    onReady: () => {
      if (wistiaRef.current) {
        wistiaRef.current.style.height = ''
      }
    },
    options,
  })

  useEffect(() => {
    if (isVisible && !isInitialized && !isThumbnailVisible) {
      prepareVideo()
    }
  }, [isVisible, isInitialized, isThumbnailVisible])

  useEffect(() => {
    if (isVisible && !isThumbnailVisible && videoApi) {
      videoApi?.play()
    }

    return () => {
      videoApi?.remove()
    }
  }, [isVisible, isThumbnailVisible, videoApi])

  useEffect(() => {
    if (!videoApi || isThumbnailVisible) {
      return
    }

    if (typeof time !== 'undefined') {
      videoApi.time(time)
    }

    if (typeof togglePlay !== 'undefined') {
      togglePlay ? videoApi.play() : videoApi.pause()
    }
  }, [togglePlay, time, videoApi, isThumbnailVisible])

  const prepareVideo = () => {
    const classes = []

    if (!controls) {
      classes.push(
        'settingsControl=false',
        'controlsVisibleOnLoad=false',
        'volumeControl=false',
        'playButton=false',
        'playbar=false',
        'smallPlayButton=false',
        'qualityControl=false',
        'fullscreenButton=false',
        'playbackRateControl=false',
        'muted=true',
      )
    }

    setIsInitialized(true)

    initWistiaVideo({
      wrapper: wistiaRef.current,
      embedId: containerId,
      autoPlay: true,
      classes,
      videoId,
    })
  }

  const handleClickThumbnail = () => {
    // Prevent content from shifting while thumbnail disappears and video appears
    if (wistiaRef.current) {
      wistiaRef.current.style.height = `${thumbnailRef.current?.offsetHeight}px`
    }

    if (isThumbnailVisible) {
      prepareVideo()
    }

    setIsThumbnailVisible(false)
  }

  const handleKeyPress = event => {
    if (event.key == KEYBOARD_KEYS.ENTER || event.key == KEYBOARD_KEYS.SPACE) {
      event.preventDefault()
      handleClickThumbnail()
    }
  }

  const getThumbnail = () => {
    return (
      <WistiaImageWrapper
        ref={thumbnailRef}
        tabIndex={0}
        role="button"
        onClick={handleClickThumbnail}
        onKeyPress={handleKeyPress}
      >
        <picture>
          <source
            srcSet={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.webp?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0&image_resize=480`}
            type="image/webp"
            media={theme.mediaQueries.v2Mobile}
          />
          <source
            srcSet={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0&image_resize=480`}
            type="image/jpeg"
            media={theme.mediaQueries.v2Mobile}
          />
          <source
            srcSet={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.webp?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`}
            type="image/webp"
          />
          <source
            srcSet={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`}
            type="image/jpeg"
          />
          <img
            src={`https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`}
            alt={thumbnailAlt}
          />
        </picture>
      </WistiaImageWrapper>
    )
  }

  return (
    <>
      {isThumbnailVisible && getThumbnail()}
      <div ref={wistiaRef} data-testid="wistia-element-ref" />
    </>
  )
}

export default Video
